import { Badge, type BadgeProps } from '@/components/ui/badge'

type Tone = NonNullable<BadgeProps['tone']>

/**
 * Domain statuses to badge tones.
 *
 * The keys are the status values the API serves, spelled as the server spells them. Anything not
 * listed falls back to neutral rather than to a guess: a status invented by a later module should
 * look unfamiliar, not accidentally reassuring.
 */
const STATUS_TONES: Record<string, Tone> = {
  // Lifecycle — healthy states
  Active: 'success',
  Approved: 'success',
  Posted: 'success',
  Balanced: 'success',
  Completed: 'success',
  Collected: 'success',
  Sent: 'success',
  Matched: 'success',
  Reconciled: 'success',
  Open: 'success',
  Present: 'success',
  Current: 'success',
  FullyRepaid: 'success',

  // In flight — awaiting someone
  Draft: 'neutral',
  Submitted: 'brand',
  UnderAppraisal: 'brand',
  PendingApproval: 'warning',
  PendingReview: 'warning',
  PendingDisbursement: 'warning',
  Pending: 'warning',
  Partial: 'warning',
  PartiallyPaid: 'warning',
  InProgress: 'brand',
  Scheduled: 'brand',
  Late: 'warning',
  Excused: 'neutral',
  Discrepant: 'warning',
  Forced: 'warning',
  Restructured: 'warning',
  Dormant: 'warning',
  Suspended: 'warning',
  Forming: 'neutral',

  // Nobody has looked at this customer yet. Warning rather than neutral, and deliberately not
  // success: an unassessed customer is not a low-risk customer, and the previous default rendered
  // every one of them as though somebody had judged them safe.
  Unassessed: 'warning',

  // Settled and inert
  Closed: 'neutral',
  Retired: 'neutral',
  Cancelled: 'neutral',
  Withdrawn: 'neutral',
  Exited: 'neutral',
  Expired: 'neutral',
  Missed: 'danger',
  Absent: 'danger',

  // Adverse
  Rejected: 'danger',
  Failed: 'danger',
  Dead: 'danger',
  Overdue: 'danger',
  Frozen: 'danger',
  Blacklisted: 'danger',
  WrittenOff: 'danger',
  Reversed: 'danger',
  Expelled: 'danger',
  Deceased: 'neutral',
  Revoked: 'danger',
  Locked: 'danger',
  Disabled: 'neutral',

  // Arrears buckets, as the classification engine emits them
  '1-30': 'warning',
  '31-60': 'warning',
  '61-90': 'danger',
  '90+': 'danger',
  OLEM: 'warning',
  Substandard: 'danger',
  Doubtful: 'danger',
  Loss: 'danger',
}

/** Splits `PendingApproval` into `Pending approval` for display without losing the raw value. */
const humanise = (status: string) =>
  status
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (first) => first.toUpperCase())
    .replace(/\s(.)/g, (match) => match.toLowerCase())

export interface StatusBadgeProps {
  status: string
  /** Overrides the mapped tone where a screen knows better than the table. */
  tone?: Tone
  /** Show the server's exact value rather than the humanised one — useful beside audit entries. */
  raw?: boolean
  className?: string
}

export function StatusBadge({ status, tone, raw = false, className }: StatusBadgeProps) {
  return (
    <Badge tone={tone ?? STATUS_TONES[status] ?? 'neutral'} className={className} title={status}>
      {raw ? status : humanise(status)}
    </Badge>
  )
}
